import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CAPSTONE } from '@rsos/constants/appNames';
import {
  RADE_MAP_CARD,
  setCardsToRender,
  setMapCardData,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { selectUserPermissionsByApp } from '@rsos/sinatra';
import { normalizeInlineRADE } from '@rsos/utils/normalizeRADE';
import fetchEmergencySession from '../../../../actions/fetchEmergencySession';
import type { RootState } from '../../../../app';
import hasMedicAlertAccess from '../../../../utils/hasMedicAlertAccess';
import {
  resolveMafLookup,
  selectLookup,
  startMafLookup,
} from '../../RADECard/radeSlice';
import type { RadeCardItem, RadeLookupState } from '../../RADECard/radeSlice';

type EmergencyDataDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

/**
 * Parse a MedicAlert lookup term. Accepts any whitespace/dash-
 * separated all-digit string (`123456`, `123-456-7890`, `123 456`)
 * and returns the cleaned digits. Returns `null` for anything else.
 *
 * NOTE: this function is intentionally permission-agnostic. The
 * caller (`GlobalMapSearch.triggerSearch`) gates the MAF lookup on
 * `medicAlert.hasAccess`, so non-MA users still get the regular
 * search path for digit-only input (e.g. phone-number lookups).
 */
export function parseMedicAlertTerm(term: string): string | null {
  const stripped = term.trim().replace(/[\s-]/g, '');
  if (!stripped) return null;
  return /^\d+$/.test(stripped) ? stripped : null;
}

interface UseMedicAlertSearch {
  /**
   * Whether the current user can view RADE results. Mirrors the gate
   * that RADECardContainer uses to decide whether to render at all,
   * so callers can avoid firing an MAF lookup the user couldn't see.
   */
  hasAccess: boolean;
  /** See {@link parseMedicAlertTerm}. */
  parse: (term: string) => string | null;
  /**
   * Background-fetch state for the most recent kickoff, mirrored from
   * `state.rade.mafLookup`. Consumers (the dropdown) read `status` to
   * decide whether to render a "Searching…" indicator, a result row,
   * or an empty state.
   */
  lookup: RadeLookupState | null;
  /**
   * Kick off a background MAF lookup. Stamps a fresh requestId into
   * `radeSlice.mafLookup` (status = `pending`). The fetch effect in
   * this hook resolves it once the API responds, dropping
   * stale-requestId responses so a fast follow-up search wins.
   *
   * Does NOT open the RAD-e map card — the user has to click the
   * resulting dropdown row for that. See {@link openCard}.
   */
  kickoff: (id: string) => void;
  /**
   * Open the RAD-e map card with the most recent MAF lookup's data.
   * Marks `state.rade.selectedLookup = 'maf'` so `RADECardNext`
   * picks up the right channel.
   */
  openCard: () => void;
  /**
   * Intentionally a no-op. Earlier this dispatched `clearMafLookup`
   * + `closeCard(RADE_MAP_CARD)`, which dismissed an already-open
   * RAD-e card every time the user cleared or edited the search bar.
   * The card now outlives the search-bar lifecycle: it persists with
   * its current data until either a fresh `kickoff` overwrites the
   * channel or the card is dismissed from its own header. Kept on
   * the public surface so existing call sites (`handleClear`,
   * `handleChange`) compile without churn.
   */
  reset: () => void;
}

/**
 * Background-fetch + click-to-open wrapper around the MedicAlert
 * (MAF) lookup. Lives in its own file so GlobalMapSearch's
 * `triggerSearch` stays readable and all MAF-specific redux glue is
 * colocated.
 *
 * The hook owns its own fetch effect: every time `mafLookup` flips
 * to a new `requestId` with `status === 'pending'`, it fires
 * `fetchEmergencySession` with `scheme: 'maf-id'` and writes the
 * normalized result back into the slice via `resolveMafLookup`. Late
 * responses are dropped via the `requestId` guard.
 */
export default function useMedicAlertSearch(): UseMedicAlertSearch {
  const dispatch = useDispatch();
  const asyncDispatch = dispatch as unknown as EmergencyDataDispatch;
  const permissions = useSelector((state: RootState) =>
    selectUserPermissionsByApp(state.sinatra.user, CAPSTONE),
  );
  const hasAccess = hasMedicAlertAccess(permissions);
  const lookup = useSelector((state: RootState) => state.rade.mafLookup);

  const kickoff = useCallback(
    (id: string) => {
      dispatch(startMafLookup({ id }));
    },
    [dispatch],
  );

  const openCard = useCallback(() => {
    dispatch(selectLookup('maf'));
    dispatch(setMapCardData(null));
    dispatch(setCardsToRender(RADE_MAP_CARD));
  }, [dispatch]);

  // See the JSDoc on `UseMedicAlertSearch.reset` for why this is a no-op.
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const reset = useCallback(() => {}, []);

  // Background fetch driver. Watches the in-flight lookup; whenever
  // a fresh (`pending`) requestId appears, kicks off the API call
  // and resolves the slice when it lands. The inflight ref guards
  // against late responses overwriting a newer lookup.
  const inflightRequestIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!lookup || !hasAccess) {
      inflightRequestIdRef.current = null;
      return;
    }
    if (lookup.status !== 'pending') return;
    if (inflightRequestIdRef.current === lookup.requestId) return;

    const { id, requestId } = lookup;
    inflightRequestIdRef.current = requestId;

    void (async () => {
      try {
        const { payload } = await asyncDispatch(
          fetchEmergencySession(id, {
            attributes: { scheme: 'maf-id' },
          }),
        );
        if (inflightRequestIdRef.current !== requestId) return;
        const items = normalizeInlineRADE(payload || {}) as RadeCardItem[];
        dispatch(
          resolveMafLookup({
            requestId,
            status: items && items.length > 0 ? 'success' : 'empty',
            data: items ?? [],
          }),
        );
      } catch (err) {
        if (inflightRequestIdRef.current !== requestId) return;
        // eslint-disable-next-line no-console -- error visibility for RADE data fetch failures
        console.error('MAF lookup failed', err);
        dispatch(
          resolveMafLookup({
            requestId,
            status: 'error',
            data: [],
          }),
        );
      }
    })();
  }, [lookup, hasAccess, asyncDispatch, dispatch]);

  return useMemo(
    () => ({
      hasAccess,
      parse: parseMedicAlertTerm,
      lookup,
      kickoff,
      openCard,
      reset,
    }),
    [hasAccess, lookup, kickoff, openCard, reset],
  );
}
